// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DropdownOption, RouteParamsWithLanguage, ZoneLanguageProps } from "../../../../../../types/screensTypes";
import Button from "../../../../../../components/ui/Button";
import { saveZone, getZoneLanguages } from "../../../../../../api/museum";
import Dropdown from "../../../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../../../context/useGlobalState";
import roomEdit from "./RoomEdit.module.css";
import stepStyles from "../../../createStep.module.css";

const ZoneLanguage: React.FC<ZoneLanguageProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { zoneId } = useParams<RouteParamsWithLanguage>(); // Get the zone ID from the URL
    const { data, loading, error } = useGlobalState();
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState<boolean>(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const saveRoom = async (): Promise<void> => {
        const token = localStorage.getItem("token"); // Retrieve token
        const zoneProcessedId = !zoneId || zoneId === "" || zoneId === "null" ? null : zoneId;
        const trackId = trackData.trackId; // Retrieve track ID
        if (!token || !trackId || !selectedContentLanguage) return;
        const saveRoomInfo = await saveZone({
            token,
            trackId,
            zoneId: zoneProcessedId,
            language: selectedContentLanguage,
        });
        if (saveRoomInfo && saveRoomInfo.zoneId) {
            const id = saveRoomInfo.zoneId;
            navigateToNext(id, selectedContentLanguage);
        }
    };

    const getExistingLanguagesAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !zoneId) return;
        const languages = await getZoneLanguages({ token, zoneId });
        const languagesArray = languages?.map((lang: { id: string }) => lang.id);
        setExistingLanguages(languagesArray || []);
    };

    useEffect(() => {
        if (zoneId && zoneId !== "" && zoneId !== "null") {
            getExistingLanguagesAPI();
        }
    }, [zoneId]);

    const languagesFiltered: DropdownOption[] = [
        ...((data.languagesAI as DropdownOption[] | undefined)?.filter((lang) =>
            existingLanguages.includes(lang.value),
        ) || []),
        { label: t("addANewLanguage"), value: "add-new-language" },
    ];

    const languagesUnfiltered: DropdownOption[] =
        (data.languagesAI as DropdownOption[] | undefined)?.filter((lang) => !existingLanguages.includes(lang.value)) ||
        [];

    const handleLanguageChange = (selectedOption: string): void => {
        if (selectedOption === "add-new-language") {
            setShowAddLanguageDropdown(true);
            setSelectedContentLanguage(null);
        } else {
            setShowAddLanguageDropdown(false);
            setSelectedContentLanguage(selectedOption);
        }
    };

    return (
        <div className={`experience-room-edit ${roomEdit.root}`}>
            <TitleSubtitle title={t("selectYourLanguage")} subtitle={""} addMargin={false} layout="step" />
            <h5 className={`experience-room-label ${roomEdit.label}`}>{t("existingLanguages")}</h5>
            <Dropdown
                label=""
                options={languagesFiltered}
                onChange={handleLanguageChange}
                loading={loading}
                error={error ? true : false}
                placeholder={t("language")}
                value={selectedContentLanguage}
                isWithoutMargin
            />
            {showAddLanguageDropdown && (
                <>
                    <h5
                        className={`experience-room-label experience-room-label--gap ${roomEdit.label} ${roomEdit.labelGap}`}
                    >
                        {t("selectANewLanguage")}
                    </h5>
                    <Dropdown
                        label=""
                        options={languagesUnfiltered}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                        isWithoutMargin
                    />
                </>
            )}
            <div className={`${stepStyles.stepButton} ${roomEdit.actions}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveRoom}
                />
            </div>
        </div>
    );
};

export default ZoneLanguage;
